import { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import {
  PaperAirplaneIcon,
  CheckIcon,
  ClipboardDocumentCheckIcon,
} from '@heroicons/react/24/outline';
import { messageThreadApi, orderApi, planApi } from '../services/api';
import { usePlatoonStore } from '../store/platoonStore';
import { useCommsStore, type CommsMessage, type CommsOrder } from '../store/commsStore';
import { PRIORITY_META, REPORT_PRIORITIES } from '../lib/reports';

type Tab = 'messages' | 'orders';

interface Thread {
  platoonId: string;
  callsign: string;
  unitId?: string | null;
  lastMessage: CommsMessage | null;
  unread: number;
  count: number;
}

function safeTime(iso?: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : format(d, 'HH:mm · dd MMM');
}
function tsMillis(iso?: string | null): number {
  const t = new Date(iso ?? '').getTime();
  return Number.isNaN(t) ? 0 : t;
}

// ─── Messages tab ────────────────────────────────────────────────────────────

function MessagesTab() {
  const queryClient = useQueryClient();
  const platoons = usePlatoonStore((s) => s.platoons);
  const liveMessages = useCommsStore((s) => s.liveMessages);
  const [selected, setSelected] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [priority, setPriority] = useState('ROUTINE');
  const [ackRequired, setAckRequired] = useState(false);

  const threadsQ = useQuery({
    queryKey: ['message-threads'],
    queryFn: async () => (await messageThreadApi.getThreads()).data?.data?.threads as Thread[],
    retry: false,
    refetchInterval: 15000,
  });

  // Merge API threads with all platoons so HQ can open a conversation with any
  // platoon, even one that has never messaged.
  const threads = useMemo(() => {
    const byId = new Map<string, Thread>();
    platoons.forEach((p) =>
      byId.set(p.id, { platoonId: p.id, callsign: p.callsign, unitId: p.unitId, lastMessage: null, unread: 0, count: 0 })
    );
    (threadsQ.data ?? []).forEach((t) => byId.set(t.platoonId, t));
    return Array.from(byId.values()).sort(
      (a, b) => tsMillis(b.lastMessage?.createdAt) - tsMillis(a.lastMessage?.createdAt)
    );
  }, [platoons, threadsQ.data]);

  const threadQ = useQuery({
    queryKey: ['messages', selected],
    queryFn: async () => (await messageThreadApi.getForPlatoon(selected!, { limit: 200 })).data?.data?.messages as CommsMessage[],
    enabled: !!selected,
    retry: false,
    refetchInterval: 10000,
  });

  // Merge snapshot + live buffer for the selected platoon, oldest → newest.
  const conversation = useMemo(() => {
    if (!selected) return [];
    const byId = new Map<string, CommsMessage>();
    [...(threadQ.data ?? []), ...liveMessages.filter((m) => m.platoonId === selected)].forEach(
      (m) => m?.id && byId.set(m.id, m)
    );
    return Array.from(byId.values()).sort((a, b) => tsMillis(a.createdAt) - tsMillis(b.createdAt));
  }, [selected, threadQ.data, liveMessages]);

  // On opening a thread, mark unread incoming (FIELD_TO_HQ) messages read.
  useEffect(() => {
    if (!selected || !threadQ.data) return;
    threadQ.data
      .filter((m) => m.direction === 'FIELD_TO_HQ' && !m.readAt)
      .forEach((m) => messageThreadApi.markRead(m.id).catch(() => {}));
  }, [selected, threadQ.data]);

  const sendMutation = useMutation({
    mutationFn: () =>
      messageThreadApi.send({ platoonId: selected!, content: draft.trim(), priority: priority as any, ackRequired }),
    onSuccess: () => {
      setDraft('');
      setAckRequired(false);
      queryClient.invalidateQueries({ queryKey: ['messages', selected] });
      queryClient.invalidateQueries({ queryKey: ['message-threads'] });
    },
    onError: (e: any) => toast.error(e?.response?.data?.error || 'Failed to send message'),
  });

  const selectedThread = threads.find((t) => t.platoonId === selected);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-4 lg:h-[calc(100vh-14rem)]">
      {/* Thread list */}
      <div className="bg-command-panel border border-command-border rounded-xl overflow-hidden flex flex-col max-h-[40dvh] lg:max-h-none">
        <div className="p-3 border-b border-command-border text-sm font-semibold text-gray-100">Threads</div>
        <div className="flex-1 overflow-y-auto divide-y divide-command-border/50">
          {threads.map((t) => (
            <button
              key={t.platoonId}
              type="button"
              onClick={() => setSelected(t.platoonId)}
              className={clsx(
                'w-full text-left px-3 py-2.5 transition-colors',
                selected === t.platoonId ? 'bg-command-accent/10' : 'hover:bg-command-border/40'
              )}
            >
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-gray-100 truncate">{t.callsign}</span>
                {t.unread > 0 && (
                  <span className="ml-auto bg-command-accent text-command-bg text-[10px] font-bold px-1.5 py-0.5 rounded-full">
                    {t.unread}
                  </span>
                )}
              </div>
              {t.lastMessage && (
                <div className="text-[11px] text-gray-500 truncate mt-0.5">
                  {t.lastMessage.direction === 'HQ_TO_FIELD' ? 'HQ: ' : ''}{t.lastMessage.content}
                </div>
              )}
            </button>
          ))}
          {threads.length === 0 && <div className="p-4 text-center text-gray-500 text-sm">No platoons.</div>}
        </div>
      </div>

      {/* Conversation */}
      <div className="bg-command-panel border border-command-border rounded-xl overflow-hidden flex flex-col min-h-[300px]">
        {selected ? (
          <>
            <div className="p-3 border-b border-command-border">
              <h2 className="text-sm font-semibold text-gray-100">{selectedThread?.callsign ?? 'Platoon'}</h2>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-2">
              {conversation.map((m) => {
                const hq = m.direction === 'HQ_TO_FIELD';
                const prio = PRIORITY_META[m.priority as keyof typeof PRIORITY_META];
                return (
                  <div key={m.id} className={clsx('flex', hq ? 'justify-end' : 'justify-start')}>
                    <div
                      className={clsx(
                        'max-w-[80%] rounded-lg px-3 py-2 text-sm',
                        hq ? 'bg-command-accent/15 border border-command-accent/30' : 'bg-command-bg border border-command-border'
                      )}
                    >
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className="text-[10px] text-gray-500 font-tactical">{hq ? 'HQ' : m.callsign}</span>
                        {prio && m.priority !== 'ROUTINE' && (
                          <span className={clsx('px-1 rounded text-[9px] font-bold', prio.badge)}>{prio.label}</span>
                        )}
                        <span className="text-[10px] text-gray-500 ml-auto">{safeTime(m.createdAt)}</span>
                      </div>
                      <div className="text-gray-100 whitespace-pre-wrap break-words">{m.content}</div>
                      {hq && (
                        <div className="mt-1 flex items-center gap-2 text-[10px] text-gray-500">
                          {m.ackRequired && (
                            <span className={clsx('inline-flex items-center gap-0.5', m.ackedAt ? 'text-command-success' : 'text-gray-500')}>
                              <ClipboardDocumentCheckIcon className="w-3 h-3" />
                              {m.ackedAt ? `Acked ${safeTime(m.ackedAt)}` : 'ACK pending'}
                            </span>
                          )}
                          {m.readAt && (
                            <span className="inline-flex items-center gap-0.5">
                              <CheckIcon className="w-3 h-3" /> Read
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
              {conversation.length === 0 && (
                <div className="py-10 text-center text-gray-500 text-sm">No messages yet. Start the conversation below.</div>
              )}
            </div>

            {/* Compose */}
            <div className="p-3 border-t border-command-border space-y-2">
              <textarea
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                placeholder="Message to field…"
                rows={2}
                className="w-full px-3 py-2 bg-command-bg border border-command-border rounded-lg text-gray-100 placeholder-gray-600 text-sm focus:outline-none focus:border-command-accent"
              />
              <div className="flex flex-wrap items-center gap-2">
                <select
                  aria-label="Priority"
                  value={priority}
                  onChange={(e) => setPriority(e.target.value)}
                  className="px-2 py-1.5 rounded bg-command-bg border border-command-border text-xs text-gray-100"
                >
                  {REPORT_PRIORITIES.map((p) => <option key={p} value={p}>{PRIORITY_META[p].label}</option>)}
                </select>
                <label className="flex items-center gap-1.5 text-xs text-gray-400">
                  <input type="checkbox" checked={ackRequired} onChange={(e) => setAckRequired(e.target.checked)}
                    className="rounded border-gray-600 w-4 h-4" />
                  Require ACK
                </label>
                <button
                  type="button"
                  onClick={() => sendMutation.mutate()}
                  disabled={!draft.trim() || sendMutation.isPending}
                  className="ml-auto inline-flex items-center gap-1.5 px-4 py-1.5 rounded-lg bg-command-accent text-command-bg text-sm font-semibold hover:bg-command-accent/90 disabled:opacity-50"
                >
                  <PaperAirplaneIcon className="w-4 h-4" />
                  {sendMutation.isPending ? 'Sending…' : 'Send'}
                </button>
              </div>
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center text-gray-500 text-sm">Select a thread to view the conversation.</div>
        )}
      </div>
    </div>
  );
}

// ─── Orders tab ────────────────────────────────────────────────────────────

function OrdersTab() {
  const queryClient = useQueryClient();
  const platoons = usePlatoonStore((s) => s.platoons);
  const liveOrders = useCommsStore((s) => s.liveOrders);
  const [platoonId, setPlatoonId] = useState('');
  const [content, setContent] = useState('');
  const [planId, setPlanId] = useState('');

  const ordersQ = useQuery({
    queryKey: ['orders'],
    queryFn: async () => (await orderApi.getAll({ limit: 100 })).data?.data?.orders as CommsOrder[],
    retry: false,
    refetchInterval: 15000,
  });

  // Draft plans are linkable to a FragO (docs/19 §3.4). Optional — tolerate 403.
  const plansQ = useQuery({
    queryKey: ['plans', 'for-orders'],
    queryFn: async () => (await planApi.getAll({ limit: 100 })).data?.data?.plans as Array<{ id: string; name: string }>,
    retry: false,
  });

  const orders = useMemo(() => {
    const byId = new Map<string, CommsOrder>();
    [...(ordersQ.data ?? []), ...liveOrders].forEach((o) => o?.id && byId.set(o.id, o));
    return Array.from(byId.values()).sort((a, b) => tsMillis(b.issuedAt) - tsMillis(a.issuedAt));
  }, [ordersQ.data, liveOrders]);

  const issueMutation = useMutation({
    mutationFn: () =>
      orderApi.issue({ platoonId, content: content.trim(), planId: planId || undefined }),
    onSuccess: () => {
      toast.success('FragO issued');
      setContent('');
      setPlanId('');
      queryClient.invalidateQueries({ queryKey: ['orders'] });
    },
    onError: (e: any) => toast.error(e?.response?.data?.error || 'Failed to issue order'),
  });

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[360px_1fr] gap-4">
      {/* Composer */}
      <div className="bg-command-panel border border-command-border rounded-xl p-4 space-y-3 h-max">
        <h2 className="text-sm font-semibold text-gray-100">Issue FragO</h2>
        <select
          aria-label="Platoon"
          value={platoonId}
          onChange={(e) => setPlatoonId(e.target.value)}
          className="w-full px-3 py-2 bg-command-bg border border-command-border rounded-lg text-gray-100 text-sm"
        >
          <option value="">Select platoon…</option>
          {platoons.map((p) => <option key={p.id} value={p.id}>{p.callsign}</option>)}
        </select>
        <select
          aria-label="Link plan"
          value={planId}
          onChange={(e) => setPlanId(e.target.value)}
          className="w-full px-3 py-2 bg-command-bg border border-command-border rounded-lg text-gray-100 text-sm"
        >
          <option value="">No linked plan (standalone FragO)</option>
          {(plansQ.data ?? []).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
        <textarea
          value={content}
          onChange={(e) => setContent(e.target.value)}
          placeholder="Order content…"
          rows={5}
          className="w-full px-3 py-2 bg-command-bg border border-command-border rounded-lg text-gray-100 placeholder-gray-600 text-sm focus:outline-none focus:border-command-accent"
        />
        <button
          type="button"
          onClick={() => issueMutation.mutate()}
          disabled={!platoonId || !content.trim() || issueMutation.isPending}
          className="w-full py-2 rounded-lg bg-command-accent text-command-bg text-sm font-semibold hover:bg-command-accent/90 disabled:opacity-50"
        >
          {issueMutation.isPending ? 'Issuing…' : 'Issue FragO'}
        </button>
      </div>

      {/* Issued orders */}
      <div className="bg-command-panel border border-command-border rounded-xl overflow-hidden flex flex-col">
        <div className="p-3 border-b border-command-border text-sm font-semibold text-gray-100">Issued Orders</div>
        <div className="overflow-y-auto p-3 space-y-2 max-h-[70vh]">
          {orders.map((o) => (
            <div key={o.id} className="rounded-lg border border-command-border bg-command-bg p-3">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm font-semibold text-gray-100">{o.callsign}</span>
                {o.plan && (
                  <span className="px-1.5 py-0.5 rounded text-[10px] bg-command-info/20 text-command-info border border-command-info/30">
                    {o.plan.name}
                  </span>
                )}
                <span
                  className={clsx(
                    'ml-auto px-2 py-0.5 rounded text-[10px] font-bold',
                    o.ackedAt
                      ? 'bg-command-success/20 text-command-success border border-command-success/30'
                      : 'bg-severity-medium/20 text-severity-medium border border-severity-medium/30'
                  )}
                >
                  {o.ackedAt ? `ACKED ${safeTime(o.ackedAt)}` : 'AWAITING ACK'}
                </span>
              </div>
              <div className="mt-1.5 text-sm text-gray-200 whitespace-pre-wrap break-words">{o.content}</div>
              <div className="mt-1 text-[10px] text-gray-500 font-tactical">Issued {safeTime(o.issuedAt)}</div>
            </div>
          ))}
          {orders.length === 0 && (
            <div className="py-10 text-center text-gray-500 text-sm">
              {ordersQ.isError ? 'Orders feed unavailable.' : 'No orders issued yet.'}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function CommsPage() {
  const [tab, setTab] = useState<Tab>('messages');
  const fetchPlatoons = usePlatoonStore((s) => s.fetchPlatoons);
  const platoons = usePlatoonStore((s) => s.platoons);
  const clearUnread = useCommsStore((s) => s.clearUnread);

  useEffect(() => {
    if (platoons.length === 0) fetchPlatoons();
    clearUnread(); // opening Comms clears the sidebar unread badge
  }, [platoons.length, fetchPlatoons, clearUnread]);

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-lg font-display font-bold text-gray-100">Comms</h1>
        <p className="text-gray-500 text-[11px]">Platoon messaging &amp; fragmentary orders</p>
      </div>

      <div className="flex gap-2">
        {([
          { id: 'messages', label: 'Messages' },
          { id: 'orders', label: 'Orders' },
        ] as { id: Tab; label: string }[]).map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={clsx(
              'px-4 py-2 rounded-lg text-sm font-medium transition-colors touch-manipulation',
              tab === t.id
                ? 'bg-command-accent text-command-bg'
                : 'bg-command-panel border border-command-border text-gray-400 hover:text-white hover:border-gray-500'
            )}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'messages' ? <MessagesTab /> : <OrdersTab />}
    </div>
  );
}
